// react
import * as React from 'react';

// internationalization
import { useTranslation } from 'react-i18next';

// tanstack
import { useParams } from '@tanstack/react-router';

// material ui icons
import AddIcon from '@mui/icons-material/Add';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';

// material ui
import {
  alpha,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

// api
import { useGetEnergyGroupEnergyTreeNodeQuery } from '@/api/generated/recalc/endpoints/v2/energyGroups';

// shared components
import { WritableOnly } from '@/components/access-control';

import { DateTime } from 'luxon';

// GoSharesDatagrid: preserves the original contract (initialRows, handleSaveChanges)
// but uses dialog-like visuals (title, content, actions) inspired by CO2CoefficientDialog.
export default function GoSharesDatagrid(props) {
  const { t } = useTranslation('energyxplorer');
  const {
    handleSaveChanges,
    onClose,
    open,
    isFetching = false,
    isLoading = false,
    nodeId,
  } = props;
  const { energyGroupId: energyGroupIdFromRoute } = useParams({
    strict: false,
  });

  const { data: energyTreeNode } = useGetEnergyGroupEnergyTreeNodeQuery(
    {
      groupId: energyGroupIdFromRoute,
      nodeId: nodeId,
    },
    { skip: !nodeId },
  );

  const initialRowsFromNodeOptions = energyTreeNode?.options?.goShares || [];
  const initialRows = initialRowsFromNodeOptions;

  // Normalize initial rows into entries: { timestamp: ISO, percentage: fraction }
  const normalize = (r) => {
    const ts =
      r.timestamp || r.timestamp_start || r.timestamp_from || r.start || null;
    let p =
      r.percentage !== undefined ? Number(r.percentage) : Number(r.value ?? 0);
    if (Number.isNaN(p)) p = 0;
    // If percentage looks like a percent (e.g. 50), convert to fraction
    if (p > 1) p = p / 100;
    return {
      timestamp: ts ? DateTime.fromISO(ts).toISO() : DateTime.now().toISO(),
      percentage: p,
    };
  };

  const initialSnapshot = React.useMemo(() => {
    return (initialRows || [])
      .map((r) => normalize(r))
      .sort(
        (a, b) =>
          DateTime.fromISO(a.timestamp).toMillis() -
          DateTime.fromISO(b.timestamp).toMillis(),
      );
  }, [initialRows]);

  const entries = initialSnapshot;
  const [draft, setDraft] = React.useState({ percentage: '', date: '' });
  const [error, setError] = React.useState('');

  const validateDraft = () => {
    const { percentage, date } = draft;
    if (!percentage && percentage !== 0) {
      setError('Angiv en procentværdi.');
      return false;
    }
    if (!date) {
      setError('Angiv en dato.');
      return false;
    }
    const p = Number(percentage);
    if (Number.isNaN(p)) {
      setError('Ugyldig procent.');
      return false;
    }
    const s = DateTime.fromISO(date);
    if (!s.isValid) {
      setError('Ugyldig dato.');
      return false;
    }
    // check overlaps
    const newStart = s;
    const overlaps = entries.some(
      (e) => DateTime.fromISO(e.timestamp).toMillis() === newStart.toMillis(),
    );
    if (overlaps) {
      setError('Der findes allerede en post for denne dato.');
      return false;
    }
    setError('');
    return true;
  };

  const addEntry = () => {
    if (!validateDraft()) return;
    const p = Number(draft.percentage);
    const fraction = p > 1 ? p / 100 : p;
    const ts = DateTime.fromISO(draft.date).toISO();
    const next = [...entries, { timestamp: ts, percentage: fraction }].sort(
      (a, b) =>
        DateTime.fromISO(a.timestamp).toMillis() -
        DateTime.fromISO(b.timestamp).toMillis(),
    );
    setDraft({ percentage: '', date: '' });
    // Persist immediately like original behavior
    handleSaveChanges(
      next.map((e) => ({ ...e, timestamp: e.timestamp, value: e.percentage })),
    );
  };

  const removeEntry = (idx) => {
    const next = entries.filter((_, i) => i !== idx);
    handleSaveChanges(
      next.map((e) => ({ ...e, timestamp: e.timestamp, value: e.percentage })),
    );
  };
  return (
    <Dialog open={open} maxWidth="md" fullWidth onClose={onClose}>
      <DialogTitle>{t('esg.go_share')}</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" gutterBottom>
          {t('esg.go_share_manage')}
        </Typography>

        <List sx={{ mb: 2 }}>
          {entries.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ px: 2 }}>
              {t('esg.no_values_yet')}
            </Typography>
          )}
          {entries.map((e, idx) => {
            const bg = (theme) => alpha(theme.palette.divider, 0.05);
            return (
              <ListItem
                sx={{ borderRadius: 2, mb: 1, bgcolor: bg }}
                key={`${e.timestamp}-${idx}`}
                secondaryAction={
                  <IconButton
                    edge="end"
                    aria-label="delete"
                    onClick={() => removeEntry(idx)}
                    sx={{ borderRadius: 2, mr: 1 }}
                  >
                    <DeleteRoundedIcon
                      sx={{ ':hover': { color: 'error.main' } }}
                    />
                  </IconButton>
                }
              >
                <ListItemText
                  primary={
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Typography variant="subtitle2">
                        {(Number(e.percentage) * 100).toFixed(1)} %
                      </Typography>
                    </Stack>
                  }
                  secondary={
                    <Typography variant="body2" color="text.secondary">
                      {DateTime.fromISO(e.timestamp).toFormat('dd-LL-yyyy')}
                    </Typography>
                  }
                />
              </ListItem>
            );
          })}
        </List>
        {(isFetching || isLoading) && <LinearProgress />}
        <Divider sx={{ my: 1 }} />

        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          {t('esg.add_new_period')}
        </Typography>
        {error && (
          <Typography
            variant="caption"
            color="error"
            sx={{ mb: 1, display: 'block' }}
          >
            {error}
          </Typography>
        )}
        <WritableOnly>
          <Grid container spacing={2} alignItems="flex-end">
            <Grid size="grow">
              <TextField
                label="GO Andel (%)"
                type="number"
                value={draft.percentage}
                onChange={(ev) =>
                  setDraft((d) => ({ ...d, percentage: ev.target.value }))
                }
                variant="filled"
                fullWidth
                slotProps={{
                  input: { endAdornment: <span>%</span> }
                }}
              />
            </Grid>
            <Grid size="grow">
              <TextField
                label={t('esg.valid_from')}
                type="date"
                value={draft.date}
                onChange={(ev) =>
                  setDraft((d) => ({ ...d, date: ev.target.value }))
                }
                variant="filled"
                fullWidth
                slotProps={{
                  inputLabel: { shrink: true }
                }}
              />
            </Grid>
            <Grid>
              <Tooltip title={t('esg.add_period')}>
                <IconButton
                  color="primary"
                  onClick={addEntry}
                  size="large"
                  sx={{ mb: 0.5, borderRadius: 2 }}
                >
                  <AddIcon />
                </IconButton>
              </Tooltip>
            </Grid>
          </Grid>
        </WritableOnly>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>
          {t('actions.close', { ns: 'common' })}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
